import { create } from "zustand";
import { fundsService } from "./fundsService";

// Monotonic id for in-flight overview fetches. Triggers can overlap (mount +
// window focus + a send settling), so only the newest request may write —
// a slow stale response must not overwrite a fresher balance.
let requestId = 0;

/**
 * Organization-wide available balance, shared so the header badge stays in
 * sync with every place funds change. The backend returns this balance under a
 * `credits` field; the store exposes it as `funds`. `funds` is null until the first
 * successful fetch — callers use that to render nothing rather than a bogus 0
 * (e.g. for roles whose token can't read the overview endpoint).
 *
 * Update paths:
 *  - fetchFunds(): re-pulls the overview; called on badge mount/window focus
 *    and after actions that change the balance server-side (campaign send,
 *    schedule cancel).
 *  - syncFromOverview(): zero-cost sync from a response someone else already
 *    fetched (the funds page re-pulls after buy/refund/3DS redirect).
 */
export const useFundsStore = create((set) => ({
   funds: null,

   fetchFunds: () => {
      const id = ++requestId;
      fundsService
         .getOverview((res) => {
            if (id !== requestId) return;
            set({ funds: res.data?.credits ?? 0 });
         })
         // Balance is a passive indicator: on failure keep whatever we last
         // knew (or stay hidden) instead of surfacing an error.
         .catch(() => {});
   },

   syncFromOverview: (overview) => {
      // This value is fresher than anything still in flight; invalidate those.
      ++requestId;
      set({ funds: overview?.credits ?? 0 });
   }
}));
